import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import { Icon, Tooltip, Typography } from 'gu-ui-kit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import urlValidator from 'utils/validators/urlValidator'
import * as Yup from 'yup'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import omit from 'lodash/omit'
import advertiserInfoValidationRulesExtended from 'modules/Forms/Account/Create/subcomponents/advertiser-info-validation-rules-extended'
import initialAdvertiserInfo from 'modules/Forms/Account/Create/initialAdvertiserInfo'
import SERVICES_API_V1 from 'api/services/v1'
import renderSelect from 'components/FormControls/select'
import GridSpinner from 'components/UI/GridSpinner'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/submitButtons'
import AdvertiserInfoGroup from 'modules/Forms/Account/Create/subcomponents/AdvertiserInfoGroup'

export const TelegramAdsSchema = () =>
  Yup.object().shape({
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    network: Yup.string().required('common::validation_messages.required'),
    advertising_subject_id: Yup.string().required(
      'common::validation_messages.required',
    ),
    promoted_url: Yup.string()
      .test(
        'urlValidator',
        'common::validation_messages.incorrect_url_format',
        urlValidator,
      )
      .required('common::validation_messages.required'),
    telegram_username: Yup.string().required(
      'common::validation_messages.required',
    ),
    showAdvertisersField: Yup.boolean(),
    advertiser_info: Yup.object().when('showAdvertisersField', {
      is: (val) => !!val === true,
      then: () =>
        Yup.reach(advertiserInfoValidationRulesExtended(), 'advertiser_info'),
      otherwise: (schema) => schema.notRequired(),
    }),
  })

let CreateTelegramAdsAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  networkType,
  networkId,
  form,
  initialValues,
  autofill,
  change,
  array,
  multiLogins,
}) => {
  const selector = formValueSelector(form)
  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit(
    form,
    onClose,
    multiLogins,
  )

  const [advertisingSubjects, setAdvertisingSubjects] = useState([])

  const {
    formValues: { email, advertiser_info, showAdvertisersField },
    user,
    i18n,
  } = useSelector((state) => ({
    formValues: selector(
      state,
      'email',
      'advertiser_info',
      'showAdvertisersField',
    ),
    user: state['app'].user,
    i18n: state['app'].i18n,
  }))

  useEffect(() => {
    user.name && email === initialValues.email && autofill('email', user.name)
  }, [user])

  const [settingsLoading, setSettingsLoading] = useState(true)

  const [advertiserSubjectsResponse, setAdvertisingSubjectsResponse] = useState(
    [],
  )

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)

      if (advertisingSubjects.length === 0) {
        SERVICES_API_V1.getNetworkSettings({ networkId })
          .then(({ response: { data } }) => {
            setAdvertisingSubjectsResponse(data?.advertising_subjects || [])
          })
          .finally(() => {
            setSettingsLoading(false)
          })
      }
    }
  }, [networkId])

  useEffect(() => {
    setAdvertisingSubjects(
      advertiserSubjectsResponse.map(({ id, name }) => ({
        value: id,
        label: name[i18n] ? name[i18n] : name['en'],
      })),
    )
  }, [advertiserSubjectsResponse, i18n])

  const submit = useCallback(
    (values) =>
      submitAccountData(
        omit(values, [
          'showAdvertisersField',
          !showAdvertisersField ? 'advertiser_info' : '',
        ]),
        multiLogins,
      ),
    [submitAccountData],
  )

  return (
    <>
      <Typography variant={'Header-60'} className={'margin-bottom-16'}>
        {t('services::networks.' + networkType)}
      </Typography>
      <Typography
        variant={'Body-20'}
        color="text-secondary"
        className={'margin-bottom-16'}
      >
        {t('services::forms.register_account.form_subtitle')}
      </Typography>
      <form onSubmit={handleSubmit(submit)} noValidate>
        <div className={'margin-bottom-64'}>
          <Field
            disabled
            name="network"
            hidden
            fullWidth
            component={renderInputField}
            t={t}
          />

          <div className={'margin-bottom-16'}>
            <LoginField
              form={form}
              multiLogins={multiLogins}
              array={array}
              autofill={autofill}
              networkId={networkId}
              label={
                'services::forms.register_account.telegram-ads.fields.login.label'
              }
            />
          </div>

          <div
            className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}
          >
            <Field
              name="promoted_url"
              type="text"
              fullWidth
              component={renderInputField}
              label={t(
                'services::forms.register_account.telegram-ads.fields.promoted_url.label',
              )}
              t={t}
            />

            <div className={formStyles['tooltip-container']}>
              <Tooltip
                title={t(
                  'services::forms.register_account.telegram-ads.fields.promoted_url.tooltip',
                )}
                placement="top"
                arrow
              >
                <Icon
                  name={'information'}
                  color={'secondary-grey'}
                  size={'m'}
                />
              </Tooltip>
            </div>
          </div>

          <div
            className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}
          >
            <Field
              label={t(
                'services::forms.register_account.telegram-ads.fields.advertising_subject_id.label',
              )}
              name="advertising_subject_id"
              component={renderSelect}
              emptyOptionLabel={t('common::select_empty_option')}
              clearOptionLabel={t('common::select_clear_option')}
              readOnly={advertisingSubjects.length == 0}
              fullWidth
              t={t}
              normalize={(value = '') => value}
              options={advertisingSubjects}
              noResultsLabel={t('common::empty_results.header')}
              ControlProps={{
                startAdornment: settingsLoading && <GridSpinner width={24} />,
              }}
            />

            <div className={formStyles['tooltip-container']}>
              <Tooltip
                title={t(
                  'services::forms.register_account.telegram-ads.fields.advertising_subject_id.tooltip',
                )}
                placement="top"
                arrow
              >
                <Icon
                  name={'information'}
                  color={'secondary-grey'}
                  size={'m'}
                />
              </Tooltip>
            </div>
          </div>

          <div
            className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}
          >
            <Field
              prefix={'@'}
              name="telegram_username"
              type="text"
              fullWidth
              component={renderInputField}
              label={t(
                'services::forms.register_account.telegram-ads.fields.telegram_username.label',
              )}
              t={t}
              format={(val) => val.replace(/^@/, '')}
            />

            <div className={formStyles['tooltip-container']}>
              <Tooltip
                title={t(
                  'services::forms.register_account.telegram-ads.fields.telegram_username.tooltip',
                )}
                placement="top"
                arrow
              >
                <Icon
                  name={'information'}
                  color={'secondary-grey'}
                  size={'m'}
                />
              </Tooltip>
            </div>
          </div>
        </div>

        <AdvertiserInfoGroup
          form={form}
          change={change}
          advertiser_info={advertiser_info}
          networkType={networkType}
          initialValues={initialValues}
          hideable={true}
        />

        <SubmitButtons
          submitted={submitAccountData.submitted}
          submitting={submitting}
          onClose={onClose}
        />
      </form>
    </>
  )
}

CreateTelegramAdsAccountForm = reduxForm({
  form: 'CreateTelegramAdsAccountForm',
  initialValues: {
    login: '',
    network: '',
    promoted_url: '',
    advertising_subject_id: null,
    telegram_username: '',
    showAdvertisersField: false,
    advertiser_info: initialAdvertiserInfo,
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateTelegramAdsAccountForm)

CreateTelegramAdsAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  change: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
}

export default CreateTelegramAdsAccountForm
